import { CareEnvironment, CarePhase } from '../../../../appModel';
import { appliedHealthCareInflation } from '../../../../appliedHealthCareInflation';
import {
  BuildYearSnapshotInput,
  FinalizeDeathBenefitInput,
  MonthComputeInput,
  PolicyProjectionStrategy,
} from '../strategyTypes';
import { DAYS_IN_MONTH } from '../sharedConstants';
import {
  computeMonthlyCostMetrics,
  computeNon2AdlDaysThisRow,
} from '../sharedEligibility';
import {
  consumeWaitingPeriodDaysForEnvironment,
  growthFactor,
  getCalendarPolicyYear,
  getCareCostAnchorYear,
  getDailyBenefitAmountForEnvironment,
  getFirstContributingPhaseStartYear,
  getPhaseCareMonthsByPhase,
  getMaxMonthlyBenefitAmount,
  getMonthlyBenefitAmountForEnvironment,
} from '../sharedPolicyConfig';
import { calculateClientAge } from '../clientAgeCalculation';
import { deriveStandardMilestones } from '../deriveMilestones';
import {
  deriveIneligibilityReason,
  deriveIneligibleDayBreakdown,
} from '../deriveIneligibilityReason';
import { buildPrePolicyMonth } from '../buildPrePolicyMonth';
import { deriveAtIssuePools, forwardBenefitPeriodTotal } from './durationPoolModel';

// ---------------------------------------------------------------------------
// NYL Asset Flex — reimbursement hybrid life with LTC rider.
//
// Two pools (Acceleration drawn first, then EOB), per-pool ACIO growth at
// policy anniversary — simple or compound per `isSimpleInflationProtection` —
// with per-pool monthly caps inflated on each
// pool's own rate (the carrier's ACIO grows the Monthly Benefit alongside
// the pools — see the p.13 ledger in docs/nyl-asset-flex — so once the draw
// moves to EOB the cap follows the EOB rate), flat Face Amount death benefit
// (depleted in direct proportion to the remaining Acceleration pool per the
// carrier's p.2 fn.2 — P2-12 — floored at Residual DB). No retroactive WP
// lump.
//
// Asset Flex's full story lives in the universal pools / waitingPeriod /
// facts / deltas — there's nothing per-claim or per-month that the universal
// shape doesn't already capture, so both state and deltas slots are empty.
// ---------------------------------------------------------------------------

export type NylAssetFlexState = Record<string, never>;
export type NylAssetFlexDeltas = Record<string, never>;

// Surfaced on `terms.dataWarnings` for partially-migrated rows (P2-13): the
// duration model is all-or-nothing, so a row with exactly one duration field
// stays fully on the legacy dollar inputs and asks for a re-extraction.
export const NYL_ASSET_FLEX_MIXED_MODEL_WARNING =
  'This Asset Flex policy has only one of the two LTC benefit duration fields, so the ' +
  'duration-derived pool model cannot be applied consistently. Both pools use the stored ' +
  'dollar amounts instead. Re-upload the illustration (or complete both duration fields) ' +
  'to enable the duration model.';

// Prior utilization drains the Acceleration (base) pool first; any excess
// spills into the EOB pool (mirrors indexedAnnuityCareStrategy's
// applyPriorUtilization). Both pools are taken at their pre-inflated
// claim-start values.
function splitPriorUtilization(
  utilizedToDate: number,
  baseAtClaimStart: number,
  eobAtClaimStart: number,
): { priorBaseUtilization: number; priorEobUtilization: number } {
  const priorBaseUtilization = Math.min(Math.max(utilizedToDate, 0), baseAtClaimStart);
  const priorEobUtilization = Math.min(
    Math.max(utilizedToDate - priorBaseUtilization, 0),
    eobAtClaimStart,
  );
  return { priorBaseUtilization, priorEobUtilization };
}

// Sub-microdollar FP residue left behind by an exhausting draw must not keep
// a pool "open" — a revived pool would earn phantom ACIO accruals at later
// anniversaries. Close-of-month balances below the tolerance snap to exactly 0.
const POOL_EXHAUSTION_TOLERANCE = 1e-6;
const snapExhaustedPool = (balance: number): number =>
  balance < POOL_EXHAUSTION_TOLERANCE ? 0 : balance;

export const nylAssetFlexStrategy: PolicyProjectionStrategy<
  NylAssetFlexState,
  NylAssetFlexDeltas
> = {
  initClaimState(client, ltcPolicy): MonthlyState<NylAssetFlexState> {
    const { baseAtIssue, eobAtIssue } = deriveAtIssuePools(ltcPolicy);
    const faceAmount = ltcPolicy.policyMaximumBenefitAmount ?? 0;
    const utilizedToDate = ltcPolicy.policyBenefitUtilizedToDate ?? 0;
    const minGuaranteed = ltcPolicy.policyMinimumGuaranteedBenefitAmount ?? 0;
    // ACIO compounds every policy anniversary regardless of whether the
    // claim has started, but the projection only spans claim months — so
    // any anniversaries that fired between policy issue and claim start
    // never get a chance to accrue inside the projection loop. Pre-inflate
    // the pools by the ACIO factor at claim start so the projection picks
    // up the carrier's actual current pool, not the year-1 capacity.
    const acio = ltcPolicy.policyInflationProtection ?? 0;
    const capYears = ltcPolicy.policyInflationProtectionYears ?? 0;
    const isSimple = ltcPolicy.isSimpleInflationProtection === true;
    const baseRate = ltcPolicy.policyInflationProtectionOnFaceAmount ?? acio;
    const eobRate = ltcPolicy.policyInflationProtectionOnCOBAmount ?? acio;
    const claimStartPolicyYear = getCalendarPolicyYear(client as Client, 0);
    const baseAtClaimStart =
      baseAtIssue * growthFactor(baseRate, capYears, isSimple, claimStartPolicyYear);
    const eobAtClaimStart =
      eobAtIssue * growthFactor(eobRate, capYears, isSimple, claimStartPolicyYear);
    const { priorBaseUtilization, priorEobUtilization } = splitPriorUtilization(
      utilizedToDate,
      baseAtClaimStart,
      eobAtClaimStart,
    );
    return {
      pools: {
        baseBenefitPool: Math.max(baseAtClaimStart - priorBaseUtilization, 0),
        extensionOfBenefitsPool: Math.max(eobAtClaimStart - priorEobUtilization, 0),
        // The Face Amount opens net of total prior utilization, floored at the
        // Residual DB — a partially used policy opens reduced.
        deathBenefit: Math.max(faceAmount - utilizedToDate, minGuaranteed),
      },
      waitingPeriod: {
        homeDays:
          ltcPolicy.homeCareWaitingPeriodDaysRemaining ??
          ltcPolicy.homeCareWaitingPeriodDays ??
          0,
        facilityDays:
          ltcPolicy.facilityCareWaitingPeriodDaysRemaining ??
          ltcPolicy.facilityCareWaitingPeriodDays ??
          0,
      },
      strategyState: {},
    };
  },

  buildClaimTerms(client, ltcPolicy): ClaimTerms {
    const baseMonthly = getMaxMonthlyBenefitAmount(ltcPolicy);
    const dailyBenefit = baseMonthly / DAYS_IN_MONTH;
    const acio = ltcPolicy.policyInflationProtection ?? 0;
    const capYears = ltcPolicy.policyInflationProtectionYears ?? 0;
    const isSimple = ltcPolicy.isSimpleInflationProtection === true;
    const clientAsClient = client as Client;
    const projectionAnchorYear = getCareCostAnchorYear(clientAsClient);
    const { baseAtIssue, eobAtIssue, usesDurationModel, hasMixedModelFields } =
      deriveAtIssuePools(ltcPolicy);
    // Prior utilization drains the pre-inflated Acceleration (base) pool
    // first; any excess spills into the EOB pool (mirrors `initClaimState`).
    // Capped at the combined claim-start capacity of both pools.
    const baseRate = ltcPolicy.policyInflationProtectionOnFaceAmount ?? acio;
    const eobRate = ltcPolicy.policyInflationProtectionOnCOBAmount ?? acio;
    const claimStartPolicyYear = getCalendarPolicyYear(clientAsClient, 0);
    const baseAtClaimStart = baseAtIssue * growthFactor(baseRate, capYears, isSimple, claimStartPolicyYear);
    const eobAtClaimStart = eobAtIssue * growthFactor(eobRate, capYears, isSimple, claimStartPolicyYear);
    const { priorBaseUtilization, priorEobUtilization } = splitPriorUtilization(
      ltcPolicy.policyBenefitUtilizedToDate ?? 0,
      baseAtClaimStart,
      eobAtClaimStart,
    );
    const priorUtilization = priorBaseUtilization + priorEobUtilization;
    return {
      strategyId: 'nyl-asset-flex',
      priorUtilization,
      nominalDailyBenefit: dailyBenefit,
      nominalDailyBenefitBase: dailyBenefit,
      // Resolve every environment through the same helper the payout math uses,
      // so the displayed cap matches the math. A raw `?? 0` would collapse a
      // sparse/unpopulated env field to a hard $0 the presentation can't rescue
      // (0 isn't nullish), misrepresenting the cap; the helper honors explicit 0
      // and falls back to the max populated daily for sparse imports.
      nominalDailyBenefitByEnvironment: {
        [CareEnvironment.home]: getDailyBenefitAmountForEnvironment(ltcPolicy, CareEnvironment.home),
        [CareEnvironment.assistedLiving]: getDailyBenefitAmountForEnvironment(
          ltcPolicy,
          CareEnvironment.assistedLiving,
        ),
        [CareEnvironment.fullCareFacility]: getDailyBenefitAmountForEnvironment(
          ltcPolicy,
          CareEnvironment.fullCareFacility,
        ),
        [CareEnvironment.independentLiving]: getDailyBenefitAmountForEnvironment(
          ltcPolicy,
          CareEnvironment.independentLiving,
        ),
      },
      // Kept populated for legacy/UI compatibility on terms.inflationRate.
      inflationRate: acio,
      inflationCapYears: capYears,
      inflationIsSimple: isSimple,
      baseBenefitPoolInflationRate: baseRate,
      baseBenefitPoolInflationCapYears: capYears,
      extensionOfBenefitsPoolInflationRate: eobRate,
      extensionOfBenefitsPoolInflationCapYears: capYears,
      baseBenefitPoolAtIssue: baseAtIssue,
      extensionOfBenefitsPoolAtIssue: eobAtIssue,
      // Only surface the durations when the duration-derived model is active
      // (all-or-nothing — P2-13). Legacy and partially-migrated policies
      // (dollar inputs) leave these undefined so the UI does not show a
      // misleading "× N months" derivation.
      baseBenefitPoolDurationMonths: usesDurationModel
        ? ltcPolicy.policyAccelerationBenefitDurationMonths ?? undefined
        : undefined,
      extensionOfBenefitsPoolDurationMonths: usesDurationModel
        ? ltcPolicy.policyEobBenefitDurationMonths ?? undefined
        : undefined,
      careCostInflationRate: appliedHealthCareInflation(clientAsClient),
      projectionAnchorYear,
      phaseStartYears: { ...(clientAsClient.clientPhasePredictedStartYears ?? {}) } as Record<
        CarePhase,
        number | null
      >,
      phaseCareMonthsByPhase: getPhaseCareMonthsByPhase(clientAsClient),
      firstPhaseStartYear: getFirstContributingPhaseStartYear(clientAsClient),
      policyPremiumStartYear: ltcPolicy.policyPremiumStartYear ?? projectionAnchorYear,
      minimumGuaranteedDeathBenefit: ltcPolicy.policyMinimumGuaranteedBenefitAmount ?? 0,
      isReimbursement: true,
      isIndemnity: false,
      ...(hasMixedModelFields ? { dataWarnings: [NYL_ASSET_FLEX_MIXED_MODEL_WARNING] } : {}),
    };
  },

  buildYearSnapshot(input: BuildYearSnapshotInput): YearSnapshot {
    const { ltcPolicy, policyYear } = input;
    const baseMonthly = getMaxMonthlyBenefitAmount(ltcPolicy);
    const acio = ltcPolicy.policyInflationProtection ?? 0;
    const capYears = ltcPolicy.policyInflationProtectionYears ?? 0;
    const isSimple = ltcPolicy.isSimpleInflationProtection === true;
    const baseRate = ltcPolicy.policyInflationProtectionOnFaceAmount ?? acio;
    const eobRate = ltcPolicy.policyInflationProtectionOnCOBAmount ?? acio;

    const baseFactor = growthFactor(baseRate, capYears, isSimple, policyYear);
    const eobFactor = growthFactor(eobRate, capYears, isSimple, policyYear);

    const { baseAtIssue, eobAtIssue, usesDurationModel } = deriveAtIssuePools(ltcPolicy);
    const inflatedBaseMonthly = baseMonthly * baseFactor;
    // The EOB monthly limit grows on the EOB rate, independent of the
    // Acceleration rate — so a 0% base / non-zero EOB policy still inflates
    // the cap during the EOB phase.
    const inflatedEobMonthly = baseMonthly * eobFactor;
    const inflatedAcceleration = baseAtIssue * baseFactor;
    const inflatedEob = eobAtIssue * eobFactor;
    const faceAmount = ltcPolicy.policyMaximumBenefitAmount ?? 0;

    // Forward-summed total a claim starting this year would pay across the
    // whole benefit period (monthly benefit keeps inflating during the claim).
    // Only the duration model has the month counts to sum over.
    const forwardLtcBenefitTotal = usesDurationModel
      ? forwardBenefitPeriodTotal(
          ltcPolicy,
          policyYear,
          baseMonthly,
          baseRate,
          eobRate,
          capYears,
          isSimple,
        )
      : undefined;

    return {
      policyYear,
      effectiveDailyBenefit: inflatedBaseMonthly / DAYS_IN_MONTH,
      effectiveDailyBenefitBase: inflatedBaseMonthly / DAYS_IN_MONTH,
      effectiveDailyBenefitRider: inflatedEobMonthly / DAYS_IN_MONTH,
      effectiveFaceAmount: faceAmount,
      effectiveContinuationBenefit: inflatedEob,
      effectiveTotalLtcBenefit: inflatedAcceleration + inflatedEob,
      monthlyBenefitCap: inflatedBaseMonthly,
      continuationMonthlyBenefitCap: inflatedEobMonthly,
      theoreticalLtcPoolCeiling: inflatedAcceleration + inflatedEob,
      ...(forwardLtcBenefitTotal !== undefined ? { forwardLtcBenefitTotal } : {}),
    };
  },

  computeMonth(
    input: MonthComputeInput<NylAssetFlexState, NylAssetFlexDeltas>,
  ): MonthlyEntry<NylAssetFlexState, NylAssetFlexDeltas> {
    const { client, ltcPolicy, phaseCarePlan, monthlyCareInput, openState, priorMonth } = input;
    const { monthIndex, carePhase, monthsIntoPhase } = monthlyCareInput;
    const clientAsClient = client as Client;

    // Cost metrics + structural policy year, resolved up front so the
    // pre-policy gate runs before any WP/ADL classification.
    const costMetrics = computeMonthlyCostMetrics(
      clientAsClient,
      phaseCarePlan,
      monthsIntoPhase,
    );
    const claimYearOffset = Math.floor(monthIndex / 12);
    const policyYear = getCalendarPolicyYear(clientAsClient, monthIndex);

    const context: MonthContext = {
      monthIndex,
      policyYear,
      clientAge: calculateClientAge(clientAsClient.intakeSurvey?.clientAge, policyYear),
      claimYearOffset,
      carePhase,
      monthsIntoPhase,
      careEnvironment: phaseCarePlan.appliedCareEnvironment,
    };

    // Pre-policy gate (see buildPrePolicyMonth). Single-life Asset Flex floors
    // its policy year at 1 via getCalendarPolicyYear, so this is defensive — a
    // care month before the premium-start year would pay $0 with full cost OOP.
    if (policyYear < 1) {
      const prePolicyFacts: MonthlyCareFacts = {
        nominalCareCost: costMetrics.nominalCareCost,
        effectiveCareCost: costMetrics.effectiveCareCost,
        dailyCareCost: costMetrics.dailyCareCost,
        careCostInflationYears: costMetrics.careCostInflationYears,
        projectedCareYear: costMetrics.projectedCareYear,
        non2AdlDays: 0,
        coverageEligibleDays: 0,
        waitingPeriodDaysConsumedHome: 0,
        waitingPeriodDaysConsumedFacility: 0,
        appliedMonthlyBenefitCap: 0,
        appliedCapPoolSource: 'base',
      };
      return buildPrePolicyMonth<NylAssetFlexState, NylAssetFlexDeltas>({
        context,
        facts: prePolicyFacts,
        openState,
        strategyDeltas: {},
      });
    }

    // 1. Day classification (2-ADL bucket → WP bucket for this care
    //    environment → eligible).
    const non2AdlDays = computeNon2AdlDaysThisRow(
      clientAsClient,
      phaseCarePlan,
      monthsIntoPhase,
    );
    const daysAfter2Adl = DAYS_IN_MONTH - non2AdlDays;

    const wpConsumption = consumeWaitingPeriodDaysForEnvironment(
      openState.waitingPeriod,
      phaseCarePlan.appliedCareEnvironment,
      daysAfter2Adl,
    );
    const {
      wpDaysThisRow,
      closeHomeWp,
      closeFacilityWp,
      waitingPeriodDaysConsumedHome,
      waitingPeriodDaysConsumedFacility,
      closeRelevantWp,
    } = wpConsumption;
    const eligibleDays = daysAfter2Adl - wpDaysThisRow;

    // 2. Per-pool monthly dollar caps for this policy year. The Acceleration
    //    cap grows on the base rate, the EOB cap on the EOB rate — so once
    //    Acceleration is exhausted and the draw moves to EOB, the monthly
    //    limit reflects EOB inflation (the carrier's ACIO keeps growing the
    //    Monthly Benefit through the EOB years) instead of staying pinned to
    //    the base rate. `costMetrics` and `policyYear` were resolved up front
    //    for the pre-policy gate.
    const acio = ltcPolicy.policyInflationProtection ?? 0;
    const capYears = ltcPolicy.policyInflationProtectionYears ?? 0;
    const isSimple = ltcPolicy.isSimpleInflationProtection === true;
    const baseRate = ltcPolicy.policyInflationProtectionOnFaceAmount ?? acio;
    const eobRate = ltcPolicy.policyInflationProtectionOnCOBAmount ?? acio;

    const baseMonthly = getMonthlyBenefitAmountForEnvironment(
      ltcPolicy,
      phaseCarePlan.appliedCareEnvironment,
    );
    const baseMonthlyCap = baseMonthly * growthFactor(baseRate, capYears, isSimple, policyYear);
    const eobMonthlyCap = baseMonthly * growthFactor(eobRate, capYears, isSimple, policyYear);

    // 3. Annual ACIO accrual at policy-year boundary. Accrual is computed
    //    against the AT-ISSUE pool amount (not the remaining pool), but
    //    only on a pool that's still open. Once a pool drains to $0 it
    //    stays exhausted — ACIO cannot reopen a closed pool. (Carrier
    //    model: the policy's lifetime LTC benefit caps at whatever was
    //    drawn before exhaustion, regardless of how the inflated ceiling
    //    would have continued to rise.) The bare `> 0` gates are reliable
    //    because close-of-month balances snap FP residue to exactly 0.
    let baseInflationAccrual = 0;
    let eobInflationAccrual = 0;
    if (priorMonth && policyYear !== priorMonth.context.policyYear) {
      const priorYear = priorMonth.context.policyYear;
      const { baseAtIssue: baseInitial, eobAtIssue: eobInitial } =
        deriveAtIssuePools(ltcPolicy);
      const baseFactorDelta =
        growthFactor(baseRate, capYears, isSimple, policyYear) -
        growthFactor(baseRate, capYears, isSimple, priorYear);
      const eobFactorDelta =
        growthFactor(eobRate, capYears, isSimple, policyYear) -
        growthFactor(eobRate, capYears, isSimple, priorYear);
      if (openState.pools.baseBenefitPool > 0) {
        baseInflationAccrual = baseInitial * baseFactorDelta;
      }
      if (openState.pools.extensionOfBenefitsPool > 0) {
        eobInflationAccrual = eobInitial * eobFactorDelta;
      }
    }
    const baseAfterAccrual = openState.pools.baseBenefitPool + baseInflationAccrual;
    const eobAfterAccrual =
      openState.pools.extensionOfBenefitsPool + eobInflationAccrual;

    // 4./5. Reimbursement with two-pool drawdown: reimburse each eligible day
    //    up to that pool's daily cap, Acceleration first then EOB.
    //    Acceleration covers as many eligible days as its pool allows (at the
    //    base cap); the remaining eligible days fall to EOB (at the EOB cap).
    //    When the two rates are equal this reduces exactly to the prior
    //    single-cap math.
    const dailyCost = costMetrics.dailyCareCost;
    const baseDailyCap = baseMonthlyCap / DAYS_IN_MONTH;
    const eobDailyCap = eobMonthlyCap / DAYS_IN_MONTH;

    const basePerDay = Math.min(dailyCost, baseDailyCap);
    const baseDaysFunded =
      basePerDay > 0 ? Math.min(eligibleDays, baseAfterAccrual / basePerDay) : 0;
    const drawFromBase = Math.min(basePerDay * baseDaysFunded, baseAfterAccrual);

    const eobPerDay = Math.min(dailyCost, eobDailyCap);
    const eobDaysFunded =
      eobPerDay > 0 ? Math.min(eligibleDays - baseDaysFunded, eobAfterAccrual / eobPerDay) : 0;
    const drawFromEob = Math.min(eobPerDay * eobDaysFunded, eobAfterAccrual);
    const ltcCoveragePaid = drawFromBase + drawFromEob;

    // 6. Closing balances. Only Acceleration drawdown depletes the death
    //    benefit, and it does so "in direct proportion to the remaining
    //    Accelerated LTC Benefits" (carrier p.2 fn.2) — the DB falls by the
    //    same percentage as the pool, not dollar-for-dollar, so the ACIO
    //    portion of a payout no longer over-depletes the DB (P2-12). Floors at
    //    the Residual DB; both models still converge at exhaustion.
    const closeBase = snapExhaustedPool(baseAfterAccrual - drawFromBase);
    const closeEob = snapExhaustedPool(eobAfterAccrual - drawFromEob);
    const minGuaranteed = ltcPolicy.policyMinimumGuaranteedBenefitAmount ?? 0;
    const unflooredCloseDeathBenefit =
      baseAfterAccrual > 0
        ? openState.pools.deathBenefit * (closeBase / baseAfterAccrual)
        : openState.pools.deathBenefit;
    const closeDeathBenefit = Math.max(unflooredCloseDeathBenefit, minGuaranteed);
    const deathBenefitReduction = openState.pools.deathBenefit - closeDeathBenefit;
    const deathBenefitFloored =
      closeDeathBenefit === minGuaranteed && unflooredCloseDeathBenefit < minGuaranteed;

    // A month drawing ANY Acceleration dollars reports the base cap; only
    // months funded purely from the EOB pool report the EOB-rate cap.
    const inEobPhase = drawFromBase <= 0 && (drawFromEob > 0 || (closeBase <= 0 && closeEob > 0));

    const facts: MonthlyCareFacts = {
      nominalCareCost: costMetrics.nominalCareCost,
      effectiveCareCost: costMetrics.effectiveCareCost,
      dailyCareCost: costMetrics.dailyCareCost,
      careCostInflationYears: costMetrics.careCostInflationYears,
      projectedCareYear: costMetrics.projectedCareYear,
      non2AdlDays,
      coverageEligibleDays: eligibleDays,
      waitingPeriodDaysConsumedHome,
      waitingPeriodDaysConsumedFacility,
      appliedMonthlyBenefitCap: inEobPhase ? eobMonthlyCap : baseMonthlyCap,
      appliedCapPoolSource: inEobPhase ? 'eob' : 'base',
    };

    const payout: PayoutSummary = {
      ltcCoveragePaid,
      ltcPayoutToCost: ltcCoveragePaid,
      ineligibleCost: costMetrics.effectiveCareCost - ltcCoveragePaid,
      indemnityPayout: 0,
    };

    const deltas: MonthlyDeltas<NylAssetFlexDeltas> = {
      payout,
      base: { depletion: drawFromBase, inflationAccrual: baseInflationAccrual },
      eob: { depletion: drawFromEob, inflationAccrual: eobInflationAccrual },
      deathBenefit: {
        reduction: deathBenefitReduction,
        flooredAtMinimum: deathBenefitFloored,
      },
      strategyDeltas: {},
    };

    const closeState: MonthlyState<NylAssetFlexState> = {
      pools: {
        baseBenefitPool: closeBase,
        extensionOfBenefitsPool: closeEob,
        deathBenefit: closeDeathBenefit,
      },
      waitingPeriod: { homeDays: closeHomeWp, facilityDays: closeFacilityWp },
      strategyState: {},
    };

    const status: MonthlyStatus = {
      ltcPoolExhausted: closeBase + closeEob <= 0,
      clientPoolExhausted: closeBase + closeEob <= 0,
      epSatisfied: closeRelevantWp === 0,
    };

    const milestones = deriveStandardMilestones<NylAssetFlexState, NylAssetFlexDeltas>({
      context,
      facts,
      openState,
      closeState,
      deltas,
      priorMonth,
      retroactiveLumpPaid: 0,
      minimumGuaranteedDeathBenefit: minGuaranteed,
      clientHasStartedLtc: clientAsClient.intakeSurvey?.clientHasStartedLtc ?? false,
      hasInflationProtection: baseRate > 0 || eobRate > 0,
    });

    const ineligibilityInput = {
      careEnvironment: phaseCarePlan.appliedCareEnvironment,
      facts,
      openState,
      closeState,
      payout,
    };
    const ineligibilityReason =
      deriveIneligibilityReason<NylAssetFlexState>(ineligibilityInput);
    const ineligibleDayBreakdown =
      deriveIneligibleDayBreakdown<NylAssetFlexState>(ineligibilityInput);

    return {
      context,
      facts,
      openState,
      state: closeState,
      deltas,
      events: { status, milestones, ineligibilityReason, ineligibleDayBreakdown },
    };
  },

  finalizeDeathBenefit({
    closingState,
  }: FinalizeDeathBenefitInput<NylAssetFlexState>): number {
    return closingState.pools.deathBenefit;
  },
};
